import React from 'react';
import { useIntl } from 'react-intl';
import { faArrowsRotate } from '@fortawesome/pro-light-svg-icons/faArrowsRotate';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { PlugBar } from '../components/PlugBar';
import { useOutreachHome } from '../hooks/useOutreachHome';
import { useOutreachProfile } from '../hooks/useOutreachProfile';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import { useProfileActions } from '../hooks/useProfileActions';
import { outreachMessages } from '../outreachMessages';
import { plugMessages } from '../plugMessages';
import { profileStateMessages as messages } from './profileStateMessages';

interface ProfileStateBarProps {
  accountId: number;
  /** Your sound shows "Find who fits" when the profile is ready; Find and the Pitch home do not. */
  showReady?: boolean;
}

/**
 * The state of the sound profile on Find, Your sound and the Pitch home (owner goal 2026-10-20;
 * built with no frame, from the Plug bar of p3 and p6): missing ("Build my sound profile"),
 * building (the busy Plug and a note), failed ("Try again" starts the build), and on Your sound the
 * next step after a build ("Find who fits" opens Find for the song to pitch).
 */
const ProfileStateBarComponent: React.FC<ProfileStateBarProps> = ({ accountId, showReady = false }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const { isDemo } = useOutreachPlan();
  const { profile } = useOutreachProfile(accountId);
  const { home } = useOutreachHome(accountId);
  const { startBuild, isBuilding } = useProfileActions(accountId);
  const song = home?.pitchSong ?? null;

  if (profile === undefined) {
    return null;
  }
  if (profile === null) {
    return (
      <PlugBar
        title={intl.formatMessage(messages.missing)}
        sub={intl.formatMessage(messages.missingSub)}
        testId="profile-state-missing"
      >
        <SMLButton
          color="primary"
          size="sm"
          omitCap
          onClick={() => startBuild(null)}
          isLoading={isBuilding}
          disabled={isDemo}
        >
          {intl.formatMessage(messages.build)}
        </SMLButton>
      </PlugBar>
    );
  }
  if (profile.status === 'building') {
    return (
      <PlugBar
        busy
        title={intl.formatMessage(plugMessages.working)}
        sub={intl.formatMessage(messages.buildingSub)}
        testId="profile-state-building"
      />
    );
  }
  if (profile.status === 'failed') {
    return (
      <PlugBar
        tone="bad"
        title={intl.formatMessage(messages.failed)}
        sub={intl.formatMessage(messages.failedSub)}
        testId="profile-state-failed"
      >
        <SMLButton
          size="sm"
          outline
          omitCap
          leftIcon={faArrowsRotate}
          onClick={() => startBuild(null)}
          isLoading={isBuilding}
          disabled={isDemo}
        >
          {intl.formatMessage(outreachMessages.tryAgain)}
        </SMLButton>
      </PlugBar>
    );
  }
  if (!showReady) {
    return null;
  }
  return (
    <PlugBar
      title={intl.formatMessage(messages.ready)}
      sub={
        song === null
          ? intl.formatMessage(messages.readySubNoSong)
          : intl.formatMessage(messages.readySub, { song: song.title })
      }
      testId="profile-state-ready"
    >
      <SMLButton
        color="primary"
        size="sm"
        omitCap
        leftIcon={faMagnifyingGlass}
        onClick={() => router.push(SMLRoutes.outreachFind, song === null ? undefined : { song: song.catalogItemUuid })}
      >
        {intl.formatMessage(messages.findWhoFits)}
      </SMLButton>
    </PlugBar>
  );
};

export const ProfileStateBar = TypedMemo(ProfileStateBarComponent);
